import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    priceDetail: 'Up to 50 employees',
    features: [
      { label: 'Org Communities', included: true },
      { label: 'Anonymous Channels', included: true },
      { label: 'Up to 50 employees', included: true },
      { label: 'Insights Dashboard', included: false },
      { label: 'SSO Integration', included: false },
      { label: 'Custom Categories', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$5',
    priceDetail: 'per seat / month',
    features: [
      { label: 'Org Communities', included: true },
      { label: 'Anonymous Channels', included: true },
      { label: 'Unlimited employees', included: true },
      { label: 'Insights Dashboard', included: true },
      { label: 'SSO Integration', included: false },
      { label: 'Custom Categories', included: false },
    ],
  },
];

const INDUSTRIES = [
  '',
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Manufacturing',
  'Retail',
  'Government',
  'Non-profit',
  'Other',
];

function OrgSignup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: form, 2: email verification, 3: success
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    orgName: '',
    domain: '',
    industry: '',
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgData, setOrgData] = useState(null);

  // If already logged in and has an org, redirect to admin
  if (user && orgData) {
    navigate('/org/admin');
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Auto-fill domain from email
    if (name === 'email' && value.includes('@')) {
      const emailDomain = value.split('@')[1];
      if (emailDomain && !formData.domain) {
        setFormData(prev => ({ ...prev, domain: emailDomain }));
      }
    }
    if (error) setError('');
  };

  // Personal email domains that are NOT allowed
  const PERSONAL_DOMAINS = new Set([
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
    'live.com', 'msn.com', 'me.com', 'mac.com', 'inbox.com',
    'gmx.com', 'gmx.net', 'fastmail.com', 'tutanota.com', 'hushmail.com',
    'yahoo.co.uk', 'yahoo.co.in', 'hotmail.co.uk', 'outlook.co.uk',
    'rediffmail.com', 'rocketmail.com', 'att.net', 'comcast.net',
    'verizon.net', 'sbcglobal.net', 'cox.net', 'charter.net',
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.firstName.trim()) { setError('Your name is required'); return; }
    if (!formData.email.trim()) { setError('Work email is required'); return; }
    if (!formData.orgName.trim()) { setError('Organization name is required'); return; }
    if (!formData.domain.trim()) { setError('Email domain is required'); return; }

    // Block personal emails
    const emailDomain = formData.email.toLowerCase().split('@')[1];
    if (!emailDomain) { setError('Please enter a valid email address.'); return; }
    if (PERSONAL_DOMAINS.has(emailDomain)) {
      setError('Please use your work email address. Personal emails (Gmail, Yahoo, Outlook, etc.) are not accepted for organization accounts.');
      return;
    }

    // Verify email domain matches
    if (emailDomain !== formData.domain.toLowerCase()) {
      setError('Your email domain must match the organization domain.');
      return;
    }

    setLoading(true);
    try {
      // First, send verification code to the work email
      const res = await axios.post('/api/auth/send-email-code', {
        email: formData.email.trim(),
        isLogin: false
      });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndCreate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Verify the email code
      await axios.post('/api/auth/verify-email-code', {
        email: formData.email.trim(),
        code: verificationCode
      });

      // Submit the org registration request (goes to review queue)
      const res = await axios.post('/api/orgs/register', {
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        orgName: formData.orgName.trim(),
        domain: formData.domain.trim().toLowerCase(),
        industry: formData.industry || undefined
      });

      setOrgData({ message: res.data.message });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <img src="/belong.png" alt="Belong" style={{ height: '56px', marginBottom: '12px' }} />
          <h1 style={styles.title}>Deploy Belong for Your Organization</h1>
          <p style={styles.subtitle}>
            Anonymous, privacy-first communities for your employees. Set up in 2 minutes.
          </p>
        </div>

        {step === 1 && (
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Your Name *</label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="e.g. Sarah" style={styles.input} required />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Work Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@yourcompany.com" style={styles.input} required />
              <small style={styles.hint}>Must be a work email — personal emails (Gmail, Yahoo, etc.) are not accepted.</small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Organization Name *</label>
              <input type="text" name="orgName" value={formData.orgName} onChange={handleChange} placeholder="e.g. Acme Corp" style={styles.input} required />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email Domain *</label>
              <input type="text" name="domain" value={formData.domain} onChange={handleChange} placeholder="yourcompany.com" style={styles.input} required />
              <small style={styles.hint}>Employees with this domain can join your organization.</small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Industry (optional)</label>
              <select name="industry" value={formData.industry} onChange={handleChange} style={styles.input}>
                {INDUSTRIES.map((ind) => (<option key={ind} value={ind}>{ind || 'Select industry...'}</option>))}
              </select>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button type="submit" disabled={loading} style={styles.submitBtn}>
              {loading ? 'Sending verification...' : 'Continue — Verify Email →'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyAndCreate}>
            <p style={{ textAlign: 'center', marginBottom: '20px', color: '#374151' }}>
              We sent a 6-digit code to <strong>{formData.email}</strong>
            </p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Verification Code</label>
              <input type="text" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder="123456" maxLength={6} style={{ ...styles.input, textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }} required />
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button type="submit" disabled={loading || verificationCode.length < 6} style={styles.submitBtn}>
              {loading ? 'Creating organization...' : 'Verify & Create Organization'}
            </button>

            <button type="button" onClick={() => { setStep(1); setError(''); }} style={{ ...styles.submitBtn, background: 'none', color: '#6b7280', border: '1px solid #d1d5db', marginTop: '12px' }}>
              ← Back
            </button>
          </form>
        )}

        {step === 3 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📨</div>
            <h2 style={{ margin: '0 0 8px', color: '#1f2937' }}>Request Submitted!</h2>
            <p style={{ color: '#6b7280', marginBottom: '16px', lineHeight: '1.6' }}>
              Your organization registration request for <strong>{formData.orgName}</strong> has been submitted for review.
            </p>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px' }}>
              Our team will review your request and get back to you at <strong>{formData.email}</strong> within 1-2 business days.
            </p>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#166534' }}>
              🛡️ We verify all organization registrations to ensure the safety and privacy of employees.
            </div>
            <button onClick={() => window.location.href = '/'} style={{ ...styles.submitBtn, marginTop: '24px' }}>
              Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 16px',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '700px',
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '15px',
    color: '#6b7280',
    margin: 0,
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  hint: {
    display: 'block',
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px',
  },
  planSection: {
    marginTop: '32px',
    marginBottom: '24px',
  },
  planTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '16px',
  },
  planGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  planCard: {
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    position: 'relative',
  },
  selectedBadge: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    backgroundColor: '#7C3AED',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '600',
    padding: '2px 8px',
    borderRadius: '10px',
  },
  planName: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 4px 0',
  },
  planPrice: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#7C3AED',
  },
  planPriceDetail: {
    fontSize: '13px',
    color: '#6b7280',
    marginBottom: '16px',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    marginBottom: '6px',
  },
  error: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '16px',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: '#7C3AED',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default OrgSignup;
